import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LayoutGrid, List } from 'lucide-react'
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'
import Button from '../ui/Button'
import ViewToggle from '../ui/ViewToggle'
import { useProjects } from '../../contexts/ProjectsContext'
import { useProjectHubViewMode } from '../../hooks/useProjectHubViewMode'
import { filterHubProjects } from '../../utils/hubProjectSearch'
import { hubOpenPath, type HubProject } from '../../api/data360'
import type { ProjectDetails } from '../../types'

type RouteMeta = { title: string; subtitle: string }

const ROUTE_META: Record<string, RouteMeta> = {
  '/resources': {
    title: 'Project Hub',
    subtitle: 'Manage projects, pipe sizes, and standards.'
  },
  '/library': {
    title: 'WPS Library',
    subtitle: 'Browse and filter approved welding procedures.'
  },
  '/wizard': {
    title: 'WPS Wizard',
    subtitle: 'Guided creation of welding procedure specifications.'
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Standards, access control, and admin catalogs.'
  }
}

const FALLBACK_META: RouteMeta = {
  title: 'Project Hub',
  subtitle: 'Manage projects, pipe sizes, and standards.'
}

function pickMeta(pathname: string): RouteMeta {
  return ROUTE_META[pathname] ?? FALLBACK_META
}

export default function TopBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { projects, hubProjects, hubSearchQuery, setHubSearchQuery } = useProjects()
  const meta = useMemo(() => pickMeta(location.pathname), [location.pathname])
  const isProjectHub = location.pathname === '/resources'
  const [viewMode, setViewMode] = useProjectHubViewMode()

  const projectHubViewOptions = useMemo(
    () => [
      { value: 'card' as const, label: 'Card View', icon: LayoutGrid },
      { value: 'list' as const, label: 'List View', icon: List }
    ],
    []
  )

  const [localQuery, setLocalQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const query = isProjectHub ? hubSearchQuery : localQuery
  const setQuery = isProjectHub ? setHubSearchQuery : setLocalQuery

  const searchableProjects = hubProjects.length > 0 ? hubProjects : projects

  const matches = useMemo(() => {
    const q = query.trim()
    if (!q) return [] as ProjectDetails[]
    return filterHubProjects(searchableProjects as HubProject[], q).slice(0, 8)
  }, [searchableProjects, query])

  useEffect(() => {
    if (location.pathname !== '/resources') {
      setHubSearchQuery('')
    }
    setLocalQuery('')
    setOpen(false)
  }, [location.pathname, setHubSearchQuery])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const goToProject = (project: ProjectDetails & HubProject) => {
    setQuery('')
    setOpen(false)
    try {
      navigate(hubOpenPath(project as HubProject))
    } catch (err) {
      console.error('Failed to open project from search:', err)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setQuery('')
      setOpen(false)
      return
    }
    if (!isProjectHub && event.key === 'Enter') {
      const first = matches[0]
      if (first) {
        event.preventDefault()
        void goToProject(first as ProjectDetails & HubProject)
      }
    }
  }

  const showDropdown = !isProjectHub && open && query.trim().length > 0

  return (
    <header className="h-[60px] bg-white/80 backdrop-blur-xl border-b border-[#E2E5EA] sticky top-0 z-40">
      <div className="h-full px-8 flex flex-col gap-3 justify-center lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            {meta.title}
          </h1>
          <p className="text-[11px] text-heritage-grey">{meta.subtitle}</p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto lg:max-w-[560px] lg:justify-end">
          <div ref={containerRef} className="relative w-full sm:w-80 lg:max-w-[340px]">
            <MagnifyingGlassIcon className="h-4 w-4 text-heritage-grey absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="input-primary pl-9 text-xs"
              placeholder="Search projects by name, client, or location"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                if (!isProjectHub) setOpen(true)
              }}
              onFocus={() => {
                if (!isProjectHub && query.trim().length > 0) setOpen(true)
              }}
              onKeyDown={handleKeyDown}
              aria-label="Search projects"
              aria-autocomplete={isProjectHub ? 'none' : 'list'}
              aria-expanded={showDropdown}
              role={isProjectHub ? 'searchbox' : 'combobox'}
            />
            {showDropdown && (
              <div
                className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-lg border border-[#E2E5EA] bg-white shadow-crc-card"
                role="listbox"
              >
                {matches.length === 0 ? (
                  <div className="px-3 py-3 text-[12px] text-heritage-grey">No matching projects</div>
                ) : (
                  <ul className="py-1">
                    {matches.map((project) => {
                      const subtitleParts = [
                        project.endClients?.[0] ?? project.client,
                        project.location
                      ].filter(Boolean) as string[]
                      return (
                        <li key={project.id}>
                          <button
                            type="button"
                            onMouseDown={(event) => {
                              event.preventDefault()
                              void goToProject(project as ProjectDetails & HubProject)
                            }}
                            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-page-bg focus:bg-page-bg focus:outline-none"
                            role="option"
                            aria-selected={false}
                          >
                            <span className="text-[13px] font-semibold text-blueprint-blue">
                              {project.name}
                            </span>
                            {subtitleParts.length > 0 && (
                              <span className="text-[11px] text-heritage-grey">
                                {subtitleParts.join(' \u2022 ')}
                              </span>
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
          {isProjectHub && (
            <ViewToggle
              value={viewMode}
              options={projectHubViewOptions}
              onChange={setViewMode}
              aria-label="Project hub view mode"
              data-testid="project-hub-view-toggle"
            />
          )}
          <Link to="/projects/new">
            <Button className="justify-center" type="button">
              <PlusIcon className="h-4 w-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>
      </div>
    </header>
  )
}
